import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Target, Star, ChevronRight } from "lucide-react";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

export const TeamGoalsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user } = useUser();
  const [teamGoals, setTeamGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamGoals = async () => {
      if (!user?.id) return;
      try {
        const teamRes = await axios.get(`${API}/coach/team?user_id=${user.id}`);
        const goalsPromises = teamRes.data.map(async (athlete) => {
          try {
            const goalsRes = await axios.get(`${API}/athlete/${athlete.id}/goals`);
            return { athlete_id: athlete.id, athlete_name: athlete.name, goals: goalsRes.data };
          } catch {
            return { athlete_id: athlete.id, athlete_name: athlete.name, goals: [] };
          }
        });
        const allTeamGoals = await Promise.all(goalsPromises);
        setTeamGoals(allTeamGoals.filter(a => a.goals.length > 0));
      } catch (e) {
        console.error("Error fetching team goals:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTeamGoals();
  }, [user?.id]);

  const getGoalAverageRating = (goal) => {
    if (!goal.ratings || goal.ratings.length === 0) return 0;
    const sum = goal.ratings.reduce((acc, r) => acc + r.rating, 0);
    return (sum / goal.ratings.length).toFixed(1);
  };

  const categoryColors = {
    technical: { bg: isDark ? 'bg-purple-500/20' : 'bg-purple-100', text: isDark ? 'text-purple-400' : 'text-purple-600' },
    physical: { bg: isDark ? 'bg-green-500/20' : 'bg-green-100', text: isDark ? 'text-green-400' : 'text-green-600' },
    tactical: { bg: isDark ? 'bg-blue-500/20' : 'bg-blue-100', text: isDark ? 'text-blue-400' : 'text-blue-600' },
    mental: { bg: isDark ? 'bg-orange-500/20' : 'bg-orange-100', text: isDark ? 'text-orange-400' : 'text-orange-600' }
  };

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-team-goals">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Team Goals</h1>
        <div className="w-10" />
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : teamGoals.length === 0 ? (
          <div className={`text-center py-12`}>
            <Target className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
            <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No goals yet</h3>
            <p className={`text-sm ${theme.textMuted}`}>Your athletes haven't set any goals</p>
          </div>
        ) : (
          <div className="space-y-4">
            {teamGoals.map((athlete) => {
              const displayedGoals = athlete.goals.slice(0, 2);
              const hasMoreGoals = athlete.goals.length > 2;
              
              return (
                <div 
                  key={athlete.athlete_id} 
                  className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                  data-testid={`athlete-card-${athlete.athlete_id}`}
                >
                  {/* Player Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{athlete.athlete_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1">
                      <h2 className={`font-semibold ${theme.textPrimary}`}>{athlete.athlete_name}</h2>
                      <p className={`text-xs ${theme.textMuted}`}>{athlete.goals.length} goal{athlete.goals.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Goals List (max 2) */}
                  <div className="space-y-2">
                    {displayedGoals.map((goal) => {
                      const avgRating = getGoalAverageRating(goal);
                      const colors = categoryColors[goal.category] || categoryColors.technical;
                      return (
                        <div 
                          key={goal.id} 
                          className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}
                          data-testid={`team-goal-${goal.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{goal.category}</span>
                              <span className={`font-medium truncate ${theme.textPrimary}`}>{goal.title}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span className={`text-sm font-medium ${theme.textPrimary}`}>{avgRating > 0 ? avgRating : '-'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* More Button */}
                  {hasMoreGoals && (
                    <button 
                      onClick={() => navigate(`/mygoal?athlete_id=${athlete.athlete_id}`)}
                      className={`w-full mt-3 py-2 rounded-xl text-sm font-medium transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/70' : 'bg-[#493986]/5 hover:bg-[#493986]/10 text-[#493986]/70'}`}
                      data-testid={`more-goals-${athlete.athlete_id}`}
                    >
                      more... ({athlete.goals.length - 2} more goal{athlete.goals.length - 2 !== 1 ? 's' : ''})
                    </button>
                  )}

                  {/* View All Button for athletes with 2 or fewer goals */}
                  {!hasMoreGoals && (
                    <button 
                      onClick={() => navigate(`/mygoal?athlete_id=${athlete.athlete_id}`)}
                      className={`w-full mt-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1 ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/70' : 'bg-[#493986]/5 hover:bg-[#493986]/10 text-[#493986]/70'}`}
                      data-testid={`view-goals-${athlete.athlete_id}`}
                    >
                      View goals <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamGoalsPage;
